/**
 * Development sign-in.
 *
 * In a Captura deployment the JWT arrives from SSO and this screen never
 * appears; it exists so the platform is testable locally without standing up
 * an identity provider.
 */

import { useState } from 'react'
import type { HealthResponse } from '../lib/types'
import { Callout, ErrorCallout } from '../components/Callout'

export function SignIn({
  onSignIn,
  health,
}: {
  onSignIn: (email: string, password: string) => Promise<void>
  health: HealthResponse | null
}) {
  const [email, setEmail] = useState('planner@demo.local')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const signIn = async () => {
    setBusy(true)
    setError(null)
    try {
      await onSignIn(email, password)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ padding: 24, maxWidth: 560, margin: '6vh auto' }} className="stack">
      <div>
        <h1>Captura AI Planner</h1>
        <div className="page-sub">
          Natural-language OOH campaign planning. The model interprets and explains; deterministic
          services calculate and optimize; Captura stays the source of truth for inventory, price
          and booking.
        </div>
      </div>

      {error ? <ErrorCallout error={error} /> : null}

      <div className="card">
        <div className="card-head">
          <h2 className="grow">Sign in</h2>
        </div>
        <div className="card-body stack">
          <div className="small muted">
            Development login uses your planner account email and password. Demo accounts use
            the password shown in the local seed output.
          </div>
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault()
              void signIn()
            }}
          >
            <label className="field">
              Email
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                type="email"
                required
              />
            </label>
            <label className="field">
              Password
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                type="password"
                required
              />
            </label>
            <button className="primary" type="submit" disabled={busy}>
              Sign in
            </button>
          </form>
        </div>
      </div>

      {health ? (
        <div className="card">
          <div className="card-head">
            <h2 className="grow">Environment</h2>
            <span className={`chip ${health.status === 'ok' ? 'ok' : 'warn'}`}>{health.status}</span>
          </div>
          <div className="card-body">
            <dl className="kv">
              <dt>Planner database</dt>
              <dd>{health.planner_db.reachable ? 'connected' : 'unreachable'}</dd>
              <dt>Captura database</dt>
              <dd>
                {health.captura_db.configured
                  ? health.captura_db.reachable
                    ? 'connected (live inventory)'
                    : 'configured but unreachable'
                  : 'not configured — planning uses the sample tenant'}
              </dd>
              <dt>Captura API</dt>
              <dd>
                {health.captura_api.configured
                  ? 'configured'
                  : 'not configured — campaigns are pre-populated, never booked'}
              </dd>
              <dt>Language model</dt>
              <dd>
                {health.llm.enabled
                  ? `${health.llm.agent_model}`
                  : 'not configured — deterministic extraction only'}
              </dd>
              <dt>Geo backend</dt>
              <dd>{health.geo_backend}</dd>
              <dt>Scoring version</dt>
              <dd className="mono">{health.scoring_version}</dd>
            </dl>
          </div>
        </div>
      ) : (
        <Callout tone="warn" title="The API is not reachable">
          Start the backend with <span className="mono">uvicorn app.main:app --port 8080</span>.
        </Callout>
      )}
    </div>
  )
}
